import type { PixelModule } from '@/runtime/types';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { EngagementSnapshot } from './contracts/engagement';
import type { HeartbeatSnapshot } from './contracts/heartbeat';
import type { IBehaviorTracker } from './contracts/IBehaviorTracker';
import type { RateLimitSnapshot } from './contracts/rateLimit';
import type { BufferSnapshot } from './contracts/buffer';
import type {
  BehaviorSignalData,
  BehaviorSignalType,
} from './contracts/signals';
import type { StateMachineSnapshot } from './contracts/stateMachine';
import type {
  SessionSummaryReason,
  SessionSummarySnapshot,
} from './contracts/sessionSummary';
import type { ViewportSnapshot } from './contracts/viewport';
import { EngagementEngine } from './engines/engagement';
import { HeartbeatEngine } from './engines/HeartbeatEngine';
import { resolveEngagementConfig } from './resolveEngagementConfig';
import { resolveHeartbeatConfig } from './resolveHeartbeatConfig';
import { resolveRuntimeIntelligenceConfig } from './resolveRuntimeIntelligenceConfig';
import { BehaviorSignalBus } from './runtime/BehaviorSignalBus';
import { BehaviorRuntimeCoordinator } from './runtime/intelligence';
import {
  ClickTracker,
  ExitIntentTracker,
  IdleTracker,
  ScrollTracker,
  VisibilityTracker,
  ViewportTracker,
} from './trackers';

export class BehaviorModule implements PixelModule {
  readonly name = 'behavior' as const;

  private trackers: IBehaviorTracker[] = [];

  private heartbeatEngine: HeartbeatEngine | null = null;

  private engagementEngine: EngagementEngine | null = null;

  private viewportTracker: ViewportTracker | null = null;

  private signalBus: BehaviorSignalBus | null = null;

  /**
   * Coordenador de Runtime Intelligence (Etapa 20). Persiste entre init/destroy
   * para manter a continuidade de geração da state machine no reinit.
   */
  private coordinator: BehaviorRuntimeCoordinator | null = null;

  private coordinatorActive = false;

  private active = false;

  init(context: PixelRuntimeContext): void {
    if (this.active) {
      context.logger.debug('[Pixel] Behavior module already initialized');
      return;
    }

    const { enabled, trackers: trackerConfig } =
      context.config.modules.behavior;

    if (!enabled) {
      context.logger.debug('[Pixel] Behavior module disabled');
      return;
    }

    context.logger.debug('[Pixel] Behavior module initialized');

    // Ordem de init (Etapa 20): consumidores antes dos produtores.
    //   1. BehaviorSignalBus
    //   2. EngagementEngine (inscreve nos sinais)
    //   3. BehaviorRuntimeCoordinator (rate limit / buffer / state machine / summary)
    //   4. Trackers (produzem VISIBLE/HIDDEN iniciais no init)
    //   5. HeartbeatEngine (fireImmediately publica HEARTBEAT no init)
    // Assim os consumidores recebem os sinais iniciais emitidos durante o init().
    const signalBus = new BehaviorSignalBus();
    this.signalBus = signalBus;

    const engagementConfig = resolveEngagementConfig(
      context.config.modules.behavior
    );

    if (engagementConfig.enabled) {
      const engine = new EngagementEngine(context, {
        config: engagementConfig,
        signalBus,
      });
      engine.init();
      this.engagementEngine = engine;
    } else {
      this.engagementEngine = null;
    }

    const intelligenceConfig = resolveRuntimeIntelligenceConfig(
      context.config.modules.behavior
    );
    const intelligenceEnabled =
      intelligenceConfig.rateLimit.enabled ||
      intelligenceConfig.buffer.enabled ||
      intelligenceConfig.stateMachine.enabled ||
      intelligenceConfig.sessionSummary.enabled;

    if (intelligenceEnabled) {
      if (!this.coordinator) {
        this.coordinator = new BehaviorRuntimeCoordinator(context, {
          config: intelligenceConfig,
          getEngagement: () => {
            const snap = this.engagementEngine?.getSnapshot();
            if (!snap) return null;
            return { score: snap.score, peakScore: snap.peakScore };
          },
        });
      }
      this.coordinator.init(signalBus);
      this.coordinatorActive = true;
    }

    const trackers: IBehaviorTracker[] = [];

    if (trackerConfig.scroll.enabled) {
      trackers.push(new ScrollTracker(context, { signalBus }));
    }
    if (trackerConfig.click.enabled) {
      trackers.push(new ClickTracker(context, { signalBus }));
    }
    if (trackerConfig.visibility.enabled) {
      trackers.push(new VisibilityTracker(context, { signalBus }));
    }
    if (trackerConfig.idle.enabled) {
      trackers.push(new IdleTracker(context, { signalBus }));
    }
    if (trackerConfig.exitIntent.enabled) {
      trackers.push(new ExitIntentTracker(context));
    }

    let viewportTracker: ViewportTracker | null = null;
    if (trackerConfig.viewport.enabled) {
      viewportTracker = new ViewportTracker(context, {
        signalBus,
        config: trackerConfig.viewport,
      });
      trackers.push(viewportTracker);
    }
    this.viewportTracker = viewportTracker;

    for (const tracker of trackers) {
      tracker.init();
    }

    this.trackers = trackers;

    const heartbeatConfig = resolveHeartbeatConfig(
      context.config.modules.behavior
    );

    if (heartbeatConfig.enabled) {
      const engine = new HeartbeatEngine(context, {
        config: heartbeatConfig,
        signalBus,
      });
      engine.init();
      this.heartbeatEngine = engine;
    } else {
      this.heartbeatEngine = null;
    }

    this.active = true;
  }

  destroy(): void {
    if (!this.active) {
      this.destroyCoordinator();
      this.engagementEngine?.destroy();
      this.engagementEngine = null;
      this.heartbeatEngine?.destroy();
      this.heartbeatEngine = null;
      this.viewportTracker = null;
      this.signalBus?.clear();
      this.signalBus = null;
      return;
    }

    // Ordem de destroy (Etapa 20): Coordinator → Engagement → Heartbeat →
    // trackers → bus. O Coordinator emite BEHAVIOR_SESSION_SUMMARY primeiro,
    // enquanto o Engagement ainda expõe score/peak válidos e o Dispatcher e o
    // SignalBus continuam disponíveis.
    this.destroyCoordinator();

    this.engagementEngine?.destroy();
    this.engagementEngine = null;

    this.heartbeatEngine?.destroy();
    this.heartbeatEngine = null;

    for (let i = this.trackers.length - 1; i >= 0; i -= 1) {
      this.trackers[i]?.destroy();
    }
    this.trackers = [];
    this.viewportTracker = null;

    this.signalBus?.clear();
    this.signalBus = null;
    this.active = false;
  }

  private destroyCoordinator(): void {
    if (this.coordinatorActive && this.coordinator) {
      this.coordinator.destroy();
    }
    this.coordinatorActive = false;
  }

  getHeartbeatSnapshot(): HeartbeatSnapshot | null {
    if (!this.heartbeatEngine) {
      return null;
    }
    return this.heartbeatEngine.getSnapshot();
  }

  getViewportSnapshot(): ViewportSnapshot | null {
    if (!this.viewportTracker) {
      return null;
    }
    return this.viewportTracker.getSnapshot();
  }

  getEngagementSnapshot(): EngagementSnapshot | null {
    if (!this.engagementEngine) {
      return null;
    }
    return this.engagementEngine.getSnapshot();
  }

  getRateLimitSnapshot(): RateLimitSnapshot | null {
    if (!this.coordinator || !this.coordinatorActive) return null;
    return this.coordinator.getRateLimitSnapshot();
  }

  getBufferSnapshot(): BufferSnapshot | null {
    if (!this.coordinator || !this.coordinatorActive) return null;
    return this.coordinator.getBufferSnapshot();
  }

  getStateMachineSnapshot(): StateMachineSnapshot | null {
    if (!this.coordinator || !this.coordinatorActive) return null;
    return this.coordinator.getStateMachineSnapshot();
  }

  getSessionSummarySnapshot(): SessionSummarySnapshot | null {
    if (!this.coordinator || !this.coordinatorActive) return null;
    return this.coordinator.getSessionSummarySnapshot();
  }

  /** @internal Avaliação manual para playground/testes. */
  evaluateEngagementNow(): void {
    this.engagementEngine?.evaluateNow();
  }

  /** @internal Flush manual do buffer (playground/testes). */
  flushBufferNow(): void {
    this.coordinator?.flushNow();
  }

  /** @internal Emissão manual de session summary (playground/testes). */
  emitSessionSummaryNow(reason: SessionSummaryReason = 'hidden'): boolean {
    return this.coordinator?.emitSummaryNow(reason) ?? false;
  }

  /** @internal Publica um sinal interno no bus (playground/testes). */
  debugPublishSignal(
    type: BehaviorSignalType,
    data?: BehaviorSignalData
  ): void {
    if (!this.signalBus) return;
    this.signalBus.publish(this.signalBus.createSignal(type, 'debug', data));
  }
}
